import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import React, { useState } from 'react';
import { EditDialog } from 'common/components/Dialog/EditDialog';
import Grid from '@mui/material/Grid';
import { styled } from '@mui/material/styles';
import { Actions, Subjects } from 'permissions/ability';
import { useAbility } from 'permissions/hooks/useAbility';
import { useSimCustomFields } from 'simInventory/hooks/useSimCustomFields';
import { useCustomFields } from 'admin/customFields/hooks/useCustomFields';
import { mapAssignedCustomFieldsToCustomField } from 'common/components/Filters/CustomFieldsFilter/customFields.utils';

import {
  findDisplayValueByValueForDropdown,
  normalizeCustomFieldLabel,
} from 'admin/customFields/utils/customFieldHelpers';
import { CustomFieldProp, FIELD_TYPE } from 'admin/customFields/entities/customFields.entity';

import { CustomFieldsEdit } from '../Inventory/Components/EditDialog/components/CustomFieldsEdit';
import { Tag } from 'src/tags/models/tags';
import { TagsAutoCompleteInput } from 'src/tags/Components/Inputs/TagsAutocompleteInput';
import { useTagsAutoCompleteApi } from '@/tags/Components/Inputs/hooks/useTagsAutoCompleteApi';
import { useIsMutating, useQueryClient } from '@tanstack/react-query';
import { tagsMutationKeys } from 'src/tags/utils/queries';
import { CARD_TYPE } from 'src/tags/models/schemas';

export type SimProductsEditDialogProps = {
  open: boolean;
  onClose: () => void;
  data: any;
};

export type SimProductsEditDialogContentProps = SimProductsEditDialogProps & {
  onTagsChange: (props: { tags: Tag[]; cardId: string }) => void;
  onCustomFieldsChange: (props: {
    customFieldsUpdate: Record<string, CustomFieldProp>;
    iccid: string;
  }) => void;
};

const GridLabelStyled = styled(Grid)({
  display: 'flex',
  alignItems: 'center',
});

export const SimProductsEditDialog = React.memo(
  ({
    open,
    data,
    onClose,
    onTagsChange,
    onCustomFieldsChange,
  }: SimProductsEditDialogContentProps) => {
    const queryClient = useQueryClient();
    const ability = useAbility();

    const iccid = data?.iccid;
    const [tagsDirty, setTagsDirty] = useState(false);
    const [customFieldChanged, setCustomFieldChanged] = useState(false);
    const {
      customFieldsData,
      assignedCustomFields,
      originalCustomFields,
      clearCustomFields,
      setAssignedCustomFields,
    } = useSimCustomFields(data?.customFields);

    const { editSimCustomFields, isMutationPending } = useCustomFields();

    const tagsEnabled = ability.can(Actions.edit, Subjects.tags);
    const isTagsMutating = useIsMutating({ mutationKey: tagsMutationKeys.assignTags });

    const [tagsAutocompleteRef, { saveTags, resetTags }] = useTagsAutoCompleteApi();

    return (
      <EditDialog
        subtitle={`SIM ${data?.iccid}`}
        unsavedChanges={tagsDirty || customFieldChanged}
        onApplyChangesClick={async () => {
          await editSimCustomFields(
            {
              iccid,
              customFields: mapAssignedCustomFieldsToCustomField(
                assignedCustomFields,
                customFieldsData,
                originalCustomFields,
              ),
            },
            {
              onSuccess: () => {
                const customFieldsUpdate: Record<string, CustomFieldProp> = {};
                for (const customFieldData of assignedCustomFields) {
                  const customFieldDescription = customFieldsData.find(
                    (customFieldDescription) =>
                      normalizeCustomFieldLabel(customFieldDescription.label) ===
                      normalizeCustomFieldLabel(customFieldData.label),
                  );

                  if (!customFieldDescription) {
                    continue;
                  }

                  const customFieldType = customFieldDescription.dataType;
                  const customFieldPropName = normalizeCustomFieldLabel(
                    customFieldDescription.label,
                  );

                  if (customFieldType === 'Dropdown') {
                    const displayValue = findDisplayValueByValueForDropdown(
                      customFieldDescription.selectionOptions,
                      customFieldData.value,
                    );

                    customFieldsUpdate[customFieldPropName] = {
                      label: customFieldDescription.label,
                      dataType: FIELD_TYPE.Dropdown,
                      textValue: null,
                      selectionOption: displayValue,
                    };
                  }

                  if (customFieldType === 'Text') {
                    customFieldsUpdate[customFieldPropName] = {
                      label: customFieldDescription.label,
                      dataType: FIELD_TYPE.Text,
                      selectionOption: null,
                      textValue: customFieldData.value,
                    };
                  }
                }

                onCustomFieldsChange({ iccid, customFieldsUpdate });
              },
            },
          );

          if (tagsEnabled) {
            await saveTags();
            queryClient.removeQueries({ queryKey: [CARD_TYPE.SIM, 'tags', 'infinity'] });
          }

          onClose();
        }}
        onCancelClick={onClose}
        onClearChangesClick={() => {
          clearCustomFields();
          resetTags();
        }}
        open={open}
        isDisabled={isMutationPending || !!isTagsMutating}
      >
        <Stack
          direction="row"
          gap={48}
          alignItems="center"
          sx={{ borderLeft: (theme) => `1px solid ${theme.palette.text.tertiary}` }}
        >
          <Grid container columnSpacing={10} rowSpacing={4} flexGrow={1}>
            {tagsEnabled && (
              <>
                <GridLabelStyled size={4}>
                  <Typography
                    variant="text2"
                    color="text.secondary"
                    sx={{
                      ml: 16,
                    }}
                  >
                    Tags
                  </Typography>
                </GridLabelStyled>
                <Grid size={8}>
                  <TagsAutoCompleteInput
                    tags={data.tags?.items || []}
                    ref={tagsAutocompleteRef}
                    onTagsChange={onTagsChange}
                    id={iccid}
                    onTagsDirtyChange={setTagsDirty}
                  />
                </Grid>
              </>
            )}
            <CustomFieldsEdit
              customFieldsData={customFieldsData}
              setChanges={setCustomFieldChanged}
              assignedCustomFields={assignedCustomFields}
              setAssignedCustomFields={setAssignedCustomFields}
            />
          </Grid>
        </Stack>
      </EditDialog>
    );
  },
);
